<template>
  <section :style="{ '--label-font-size': labelFontSize }">
    <l-map
      v-if="showElement && !geojson"
      :style="`height: ${height}; width: ${width}`"
      :center="center"
      :options="leafletOptions"
      :bounds="bounds"
      :zoom="zoom"
      @update:zoom="currentZoom = $event"
      @ready="handleReady(leafletMap)"
      ref="dcMap"
    >
      <l-tile-layer v-bind="basemap" />

      <!-- geoJSON layer (zonepart/sector) center drivescout icon markers   -->
      <l-feature-group v-if="showCenterMarkers" ref="zoneCenterMarkersGroup">
        <l-marker
          v-for="(marker, index) in zoneCenterMarkers"
          :lat-lng="marker"
          :icon="icon"
        >
          <l-tooltip :content="marker.popupData"></l-tooltip>
        </l-marker>
      </l-feature-group>

      <!-- markers without associated rendered zones passed in mapMarkers prop  -->
      <l-feature-group v-if="!renderMarkersOnCanvas" ref="markersGroup">
        <l-marker
          v-for="(marker, index) in markers"
          :key="index"
          :lat-lng="marker"
          :icon="markerIcon(marker)"
          @mouseover="hoveredUserId = marker.user_id"
          @mouseout="hoveredUserId = null"
        >
          <l-tooltip :content="marker.popupData"></l-tooltip>
        </l-marker>
      </l-feature-group>

      <!-- high-volume mode: render all markers on a single canvas layer (one
           layer instead of thousands of components) -->
      <l-geo-json
        v-if="renderMarkersOnCanvas && markerGeoJson"
        :geojson="markerGeoJson"
        :options="canvasMarkerOptions"
      >
      </l-geo-json>

      <!-- geojson layers/markers (zones)   -->
      <l-geo-json
        v-if="!minimal"
        v-for="(layer, index) in geoJSONLayers"
        :geojson="layer"
        :options="geoJSONOptions"
        :optionsStyle="layer.style"
      >
      </l-geo-json>
      <!--  markers with geoJSON layer zone names ex. "A", "A1", etc  -->
      <l-marker
        v-if="!minimal"
        v-for="(marker, index) in geoJSONMarkers"
        :lat-lng="marker.center"
        :icon="marker.icon"
      >
      </l-marker>

      <!--  zones legend  -->
      <l-control v-if="!minimal && showLegend" position="bottomright">
        <div class="map-info map-legend">
          <span v-for="(zone, index) in zones" :key="index">
            <i
              class="zone-theme"
              :style="`background-color: ${zone.theme.fill}`"
            ></i>
            {{ zone.name }}
          </span>
        </div>
      </l-control>
    </l-map>

    <l-map
      v-if="showElement && geojson"
      :style="`height: ${height}; width: ${width}`"
      :options="leafletOptions"
      :bounds="bounds"
      :zoom="zoom"
      @update:zoom="currentZoom = $event"
      @ready="handleReady(leafletMap)"
      ref="dcMap"
    >
      <l-tile-layer v-bind="basemap" />

      <!-- geojson layers/markers (zones)   -->
      <l-geo-json :geojson="geojson" :options="geoJSONOptions"> </l-geo-json>
    </l-map>
  </section>
</template>

<script>
import { latLngBounds, latLng } from "leaflet";
import {
  LMap,
  LMarker,
  LControl,
  LIcon,
  LGeoJson,
  LFeatureGroup,
  LPopup,
  LTooltip,
  LTileLayer,
} from "vue2-leaflet";
import flattenDeep from "lodash/flattenDeep";
import { isJson } from "../../../helpers/json";
import { BASEMAP } from "../../../../../DriveScout/Config.js";
import ColorHash from "color-hash";
const colorHash = new ColorHash();

export default {
  name: "dcMap",
  components: {
    LMap,
    LMarker,
    LControl,
    LIcon,
    LGeoJson,
    LFeatureGroup,
    LPopup,
    LTooltip,
    LTileLayer,
  },
  props: {
    //context used for specific maps like the admin view student's pickup locations map
    context: { type: String },
    height: { type: String, required: true },
    showCenterMarkers: { type: Boolean, default: true },
    isSas: { type: Boolean, default: false },
    mapCenter: {
      type: Object,
      default: function () {
        return {};
      },
    },
    mapLocations: {
      type: Array,
      default: function () {
        return [];
      },
    },
    mapMarkers: {
      type: Array,
      default: function () {
        return [];
      },
    },
    mapSchool: {
      type: Object,
      default: function () {
        return {};
      },
    },
    geojson: { type: Object },
    minimal: { type: Boolean, default: false },
    // Opt-in for marker-heavy maps (e.g. the admin pickup-locations report):
    // draws every marker on one canvas layer instead of a component per point.
    renderMarkersOnCanvas: { type: Boolean, default: false },
    showElement: { type: Boolean, default: true },
    showLegend: { type: Boolean, default: false },
    width: { type: String, required: true },
  },
  data() {
    return {
      basemap: BASEMAP,
      hoveredUserId: null,
      geoJSONMarkers: [],
      geoJSONOptions: {
        //set center icon of each geojson layer
        onEachFeature: (feature, layer) => {
          const marker = {};
          const polygonCenter = layer.getBounds().getCenter();
          marker.center = polygonCenter;
          //layer.bindPopup(geomData.label);
          marker.icon = L.divIcon({
            className: "map-div-icon geojson-marker",
            html: "<span>" + feature.name + "</span>",
          });
          this.geoJSONMarkers.push(marker);
        },
      },
      icon: L.icon({
        iconUrl: "images/map-pin.png",
        iconSize: [28, 45],
        iconAnchor: [14, 45],
        className: "location-marker",
      }),
      highlightIcon: L.icon({
        iconUrl: "images/map-pin.png",
        iconSize: [28, 45],
        iconAnchor: [14, 45],
        className: "location-marker location-marker--highlighted",
      }),
      //latlngs array used to set bounds when all parts have loaded
      latlngs: [],
      leafletOptions: {
        fullscreenControl: true,
        // Canvas rendering only matters when we draw the high-volume marker layer.
        preferCanvas: this.renderMarkersOnCanvas,
      },
      // Renders each point feature as a lightweight canvas circle marker; home
      // addresses are tinted differently from pickup locations.
      canvasMarkerOptions: {
        pointToLayer: (feature, latlng) => {
          const marker = L.circleMarker(latlng, {
            radius: 5,
            fillColor: feature.properties.isHomeAddress ? "#2b8cbe" : "#5cb85c",
            color: "#ffffff",
            weight: 1,
            fillOpacity: 0.85,
          });
          if (feature.properties.popupData) {
            marker.bindTooltip(feature.properties.popupData);
          }
          return marker;
        },
      },
      //zones used for map legend
      currentZoom: 12,
      zones: [],
      zoom: 12,
    };
  },
  methods: {
    markerIcon: function (marker) {
      if (
        this.hoveredUserId &&
        marker.user_id &&
        marker.user_id === this.hoveredUserId
      ) {
        return this.highlightIcon;
      }
      return this.icon;
    },
    makeZoneTheme: function (zone, index) {
      if (zone.bg_color) {
        return {
          fill: zone.bg_color,
          textColor: zone.bg_color,
          border: zone.bg_color,
        };
      }
      const fill = colorHash.hex(zone.name + "-" + index.toString());
      const textColor = colorHash.hex(zone.name + "-" + (index + 1).toString());
      const border = colorHash.hex(zone.name + "-" + (index + 2).toString());
      return {
        fill: fill,
        textColor: textColor,
        border: border,
      };
    },
    setZoneThemes: function (locations) {
      let polygons = [];
      let zoneCounter = 0;

      polygons = locations.map((location) => {
        //set theme/color by zone
        if (location.hasOwnProperty("zones") && location.zones) {
          return location.zones.map((zone) => {
            zone.theme = this.makeZoneTheme(zone, zoneCounter);
            this.zones.push(zone);
            zoneCounter = zoneCounter + 3;
            //set geometry/outline for each zonepart/sector
            if (zone.hasOwnProperty("zoneparts")) {
              return zone.zoneparts.map((zonepart) => {
                return this.setLayerStyle({
                  label: zonepart.name,
                  theme: zone.theme,
                  geom: isJson(zonepart.geom)
                    ? JSON.parse(zonepart.geom)
                    : zonepart.geom,
                });
              });
            }
          });
        } else {
          return [];
        }
      });
      return flattenDeep(polygons);
    },
    handleReady: function (map) {},
    setLayerStyle: function (layer) {
      layer.geom.style = {
        name: layer.geom.name,
        fillColor: layer.theme.fill,
        weight: 2,
        size: 15,
        opacity: 1,
        color: layer.theme.textColor,
        dashArray: "3",
        fillOpacity: 0.4,
      };
      return layer;
    },
    mapSetZoom: function (map, zoom = null) {
      if (zoom) {
        map.setZoom(zoom);
      } else {
        this.minimal ? map.setZoom(13) : map.setZoom(12);
      }
    },
  },
  mounted() {},
  beforeDestroy() {
    //remove all event listeners because vue2-leaflet doesnt do it properly
    this.leafletMap.off();
  },
  destroyed() {},
  created() {},
  computed: {
    labelFontSize() {
      if (this.currentZoom <= 9) return "12px";
      if (this.currentZoom <= 10) return "14px";
      if (this.currentZoom <= 11) return "16px";
      if (this.currentZoom <= 12) return "20px";
      return "24px";
    },
    // making these computed properties instead of part of the regular data object makes them reactive to changes in the angular state
    bounds: function () {
      if (this.latlngs.length > 1) {
        return latLngBounds(this.latlngs);
      } else {
        return null;
      }
    },
    center: function () {
      return this.mapCenter;
    },
    geoJSONLayers: function () {
      //each layer is a ZONEPART/SECTOR not a zone
      if (this.minimal || !this.locations.length) {
        return [];
      }

      let polygons = this.setZoneThemes(this.locations);

      let latlngs = [];
      let layers = [];
      if (polygons.length) {
        polygons.forEach((polygon) => {
          polygon.geom.name = polygon.label;
          // loop over the coordinates array to expand the visible field of the map
          polygon.geom.coordinates.forEach((coords) => {
            coords.forEach((coordinates) => {
              if (coordinates.length !== 2) {
                coordinates.forEach((point) => {
                  latlngs.push(L.GeoJSON.coordsToLatLng(point));
                });
              } else {
                latlngs.push(L.GeoJSON.coordsToLatLng(coordinates));
              }
            });
          });
          // add geojson layer to array to show sectors/zones on map
          layers.push(polygon.geom);
        });
      }
      this.latlngs = latlngs;

      //remove duplicate zoneparts so they only get drawn once
      layers = Array.from(new Set(layers.map((layer) => layer.name))).map(
        (name) => {
          return layers.find((layer) => layer.name === name);
        },
      );
      return layers;
    },
    leafletMap: function () {
      return this.$refs.dcMap.mapObject;
    },
    locations: function () {
      return this.mapLocations;
    },
    zoneCenterMarkers: function () {
      const centroidMarkers =
        this.locations.map((location) => {
          const zones = location.zones || [];
          return zones
            .map((zone) => {
              if (!zone.centroid) return null;
              const marker = L.latLng({
                lng: zone.centroid.coordinates[0],
                lat: zone.centroid.coordinates[1],
              });
              let html = "Center of Zone " + zone.name + "<br />";
              html += "<p>This is for service are calculation</p>";
              marker.popupData = html;

              return marker; //zone.centroid.coordinates;
            })
            .filter((m) => m !== null);
        })[0] || [];

      let markers = this.locations.map((location) => {
        if (isJson(location.address)) {
          location.address = JSON.parse(location.address);
        }
        const marker = L.latLng(location.lat, location.lng);
        let html = location.name + "<br />";
        html += location.address.street + "<br />";
        html += location.address.city + " ";
        html += location.address.state + " ";
        html += location.address.zip;
        marker.popupData = html;
        this.latlngs.push({ lat: location.lat, lng: location.lng });
        return marker;
      });
      return markers; //[...markers,...centroidMarkers];
    },
    // One GeoJSON FeatureCollection for the canvas marker layer. Built only
    // when renderMarkersOnCanvas is set; also seeds latlngs for bounds, since
    // the per-marker `markers` computed is not evaluated in canvas mode.
    markerGeoJson: function () {
      if (!this.renderMarkersOnCanvas) {
        return null;
      }

      const latlngs = [];
      const features = this.mapMarkers
        .map((location) => {
          const lat = parseFloat(location.lat);
          const lng = parseFloat(location.lng);
          if (!isFinite(lat) || !isFinite(lng)) {
            return null;
          }

          let html = location.popupData;
          if (!html) {
            html = location.name + "<br />";
            if (location.address) {
              html += (location.address.street || "") + "<br />";
              html += (location.address.city || "") + " ";
              html += (location.address.state || "") + " ";
              html += location.address.zip || "";
            }
          }

          latlngs.push({ lat: lat, lng: lng });
          return {
            type: "Feature",
            geometry: { type: "Point", coordinates: [lng, lat] },
            properties: {
              popupData: html,
              user_id: location.user_id,
              isHomeAddress: !!location.isHomeAddress,
            },
          };
        })
        .filter((feature) => feature !== null);

      this.latlngs = latlngs;

      if (!features.length) {
        return null;
      }
      return { type: "FeatureCollection", features: features };
    },
    markers: function () {
      //this is used for the map-markers prop- will show a marker without the zone circle
      return this.mapMarkers
        .map((location) => {
          //let marker = L.marker([location.lat, location.lng]); //.addTo(mymap);
          const marker = L.latLng(location.lat, location.lng);
          let html = location.name + "<br />";
          html += location.address.street + "<br />";
          html += location.address.city + " ";
          html += location.address.state + " ";
          html += location.address.zip;
          marker.popupData = html;
          marker.user_id = location.user_id;
          return marker;
        })
        .filter((marker) => marker !== null); // Remove invalid markers
    },
    school: function () {
      return this.mapSchool;
    },
  },
  watch: {
    geojson: function (newGeojson, oldGeojson) {
      console.log(newGeojson, oldGeojson);
      if (this.geojson.features.length > 0) {
        this.latlngs = [];
        this.geojson.features.forEach((feature) => {
          feature.geometry.features.forEach((f) => {
            if (f.geometry.type === "Point") {
              this.latlngs.push(
                L.GeoJSON.coordsToLatLng(f.geometry.coordinates),
              );
            }

            if (f.geometry.type === "MultiPolygon") {
              f.geometry.coordinates.forEach((points) => {
                points.forEach((pts) => {
                  pts.forEach((p) => {
                    this.latlngs.push(L.GeoJSON.coordsToLatLng(p));
                  });
                });
              });
            }

            if (f.geometry.type === "Polygon") {
              f.geometry.coordinates.forEach((points) => {
                points.forEach((point) => {
                  this.latlngs.push(L.GeoJSON.coordsToLatLng(point));
                });
              });
            }
          });
        });
        const ll = latLngBounds(this.latlngs);
        if (this.$refs.dcMap) {
          this.leafletMap.fitBounds(ll);
        }
      }
    },
  },
};
</script>

<style scoped>
:deep(.map-div-icon) {
  background: transparent !important;
  border: none !important;
  font-weight: bold;
  -webkit-text-stroke: 1px rgba(0, 0, 0, 0.8);
  color: white;
  font-size: var(--label-font-size, 20px);
  white-space: nowrap;
}
:deep(.location-marker--highlighted) {
  filter: drop-shadow(0 0 4px #3388ff) drop-shadow(0 0 8px #3388ff);
}
:deep(.leaflet-tooltip) {
  font-size: 16px;
}
</style>
